import * as XLSX from 'xlsx';

// Export products to Excel
export const exportProductsToExcel = (products, categories) => {
  // Create a category map for easy lookup
  const categoryMap = {};
  categories.forEach(cat => {
    categoryMap[cat._id] = cat.name;
  });

  // Prepare data for Excel
  const excelData = products.map(product => ({
    'Title': product.title || product.name || '',
    'Slug': product.slug || '',
    'Description': product.description || '',
    'Category': product.category ? (categoryMap[product.category._id] || product.category.name || '') : '',
    'Subcategory': product.subcategory || '',
    'Price': product.price || 0,
    'MRP': product.mrp || '',
    'Images': product.images ? JSON.stringify(product.images) : '',
    'Variants': product.variants && product.variants.length > 0 
      ? JSON.stringify(product.variants.map(v => ({
          sku: v.sku || '',
          sizeLabel: v.sizeLabel || v.label || '',
          price: v.price || product.price || 0,
          stock: v.stock || 0
        })))
      : ''
  }));

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');

  // Set column widths
  const columnWidths = [
    { wch: 30 }, // Title
    { wch: 30 }, // Slug
    { wch: 50 }, // Description
    { wch: 20 }, // Category
    { wch: 20 }, // Subcategory
    { wch: 10 }, // Price
    { wch: 10 }, // MRP
    { wch: 60 }, // Images
    { wch: 60 }  // Variants
  ];
  worksheet['!cols'] = columnWidths;

  // Generate Excel file and download
  const fileName = `products_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

// Import products from Excel
export const importProductsFromExcel = (file, categories) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Create category map
        const categoryMap = {};
        categories.forEach(cat => {
          categoryMap[cat.name.toLowerCase()] = cat._id;
        });

        // Transform Excel data to product format
        const products = jsonData.map(row => {
          // Find category ID by name
          const categoryName = (row.Category || '').toString().trim();
          const categoryId = categoryMap[categoryName.toLowerCase()] || null;

          // Parse images
          let images = [];
          if (row.Images) {
            try {
              images = typeof row.Images === 'string' 
                ? JSON.parse(row.Images) 
                : row.Images;
            } catch (e) {
              // If it's a comma-separated string, split it
              if (typeof row.Images === 'string') {
                images = row.Images.split(',').map(url => url.trim()).filter(Boolean);
              }
            }
          }

          // Parse variants
          let variants = [];
          if (row.Variants) {
            try {
              variants = typeof row.Variants === 'string'
                ? JSON.parse(row.Variants)
                : row.Variants;
            } catch (e) {
              console.warn('Failed to parse variants:', e);
            }
          }

          return {
            title: row.Title || '',
            slug: row.Slug || generateSlug(row.Title || ''),
            description: row.Description || '',
            category: categoryId,
            subcategory: row.Subcategory || '',
            price: parseFloat(row.Price) || 0,
            mrp: row.MRP ? parseFloat(row.MRP) : undefined,
            images: images,
            variants: variants.length > 0 ? variants : undefined
          };
        }).filter(product => product.title); // Filter out empty products

        resolve(products);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
};

// Export categories to Excel
export const exportCategoriesToExcel = (categories) => {
  // Prepare data for Excel
  const excelData = categories.map(category => ({
    'Name': category.name || '',
    'Slug': category.slug || '',
    'Subcategories': category.subcategories && category.subcategories.length > 0
      ? JSON.stringify(category.subcategories.map(sub => ({
          name: sub.name || '',
          slug: sub.slug || ''
        })))
      : ''
  }));

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Categories');

  // Set column widths
  const columnWidths = [
    { wch: 30 }, // Name
    { wch: 30 }, // Slug
    { wch: 60 }  // Subcategories
  ];
  worksheet['!cols'] = columnWidths;

  // Generate Excel file and download
  const fileName = `categories_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

// Import categories from Excel
export const importCategoriesFromExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Transform Excel data to category format
        const categories = jsonData.map(row => {
          // Parse subcategories
          let subcategories = [];
          if (row.Subcategories) {
            try {
              subcategories = typeof row.Subcategories === 'string'
                ? JSON.parse(row.Subcategories)
                : row.Subcategories;
            } catch (e) {
              console.warn('Failed to parse subcategories:', e);
            }
          }

          return {
            name: row.Name || '',
            slug: row.Slug || generateSlug(row.Name || ''),
            subcategories: subcategories
          };
        }).filter(category => category.name); // Filter out empty categories

        resolve(categories);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
};

// Helper function to generate slug
const generateSlug = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

